import {
  AfterViewInit,
  Component,
  HostListener,
  inject,
  input,
  signal,
  computed,
  OnDestroy,
  ElementRef,
  ViewChildren,
  QueryList,
  ChangeDetectionStrategy,
  effect,
} from '@angular/core';
import { Router, Route } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CommandPaletteService } from 'libs/core/src/lib/services/command-palette.service';
import { GlobalSearchService } from 'libs/core/src/lib/services/global-search.service';
import { KNOWN_POLICY_ROUTE_SUBTYPES } from 'libs/core/src/lib/utils/entity-routes';
import { IGlobalSearchResult, SearchResultType } from '@inshuwa-client/models';
import {
  LucideArrowRight,
  LucideBuilding,
  LucideCar,
  LucideCreditCard,
  LucideFileCheck,
  LucideHandshake,
  LucideShield,
  LucideUser,
  LucideUsers,
} from '@lucide/angular';
import { Subject, Subscription, catchError, debounceTime, distinctUntilChanged, of, switchMap } from 'rxjs';

export interface CommandItem {
  label: string;
  route: string;
  category: string;
  keywords?: string[];
  subtitle?: string;
  relatedTo?: string;
}

export function routesToCommandItems(
  routes: Route[],
  prefix = '',
): CommandItem[] {
  const items: CommandItem[] = [];

  for (const route of routes) {
    if (route.path === '' && !route.children) continue;

    const routePrefix = [prefix, route.path].filter(Boolean).join('/');
    const children = route.children ?? [route];

    for (const child of children) {
      if (!child.path || child.path === '' || child.path.includes(':') || child.redirectTo) continue;

      const label = (child.title as string) ?? child.path;

      items.push({
        label,
        route: child.path.startsWith('dashboard/') ? child.path : `${routePrefix}/${child.path}`,
        category: 'Navigation',
      });
    }
  }

  return items;
}

const ENTITY_CATEGORY_LABELS: Record<SearchResultType, string> = {
  Client: 'Clients',
  Vehicle: 'Vehicles',
  Policy: 'Policies',
  Transaction: 'Transactions',
  Claim: 'Claims',
  Intermediary: 'Intermediaries',
  Insurer: 'Insurers',
  Partner: 'Partners',
};

// Policy subtypes without a detail route (e.g. goods-in-transit, house-owner) fall back to the policies list.

export function buildEntityResultRoute(result: IGlobalSearchResult): string {
  switch (result.type) {
    case 'Client':
      return `/dashboard/clients/${result.id}/${(result.subtype ?? 'individual').toLowerCase()}`;
    case 'Vehicle':
      // Vehicles have no standalone detail page — land on the owning client's page instead.
      return result.clientId
        ? `/dashboard/clients/${result.clientId}/${(result.subtype ?? 'individual').toLowerCase()}`
        : '/dashboard/clients';
    case 'Policy': {
      const subtype = result.subtype?.toLowerCase();
      return subtype && KNOWN_POLICY_ROUTE_SUBTYPES.has(subtype)
        ? `/dashboard/policies/${subtype}/${result.id}`
        : '/dashboard/policies';
    }
    // Claims/Transactions have no detail route today — land on their list view.
    case 'Transaction':
      return '/dashboard/transactions';
    case 'Claim':
      return '/dashboard/claims';
    case 'Intermediary': {
      // Agents belong to exactly one insurer, and the agent detail page reads insurerId
      // from the query string (needed for admin-portal users, who have no insurer of
      // their own) — brokers can belong to several insurers, so there's nothing to pass.
      const path = `/dashboard/intermediaries/${result.subtype === 'broker' ? 'brokers' : 'agents'}/${result.id}`;
      return result.subtype === 'agent' && result.insurerId
        ? `${path}?insurerId=${result.insurerId}`
        : path;
    }
    case 'Insurer':
      return `/dashboard/insurers/${result.id}`;
    case 'Partner':
      return `/dashboard/partners/${result.id}`;
    default:
      return '/dashboard';
  }
}

export function resultsToCommandItems(results: IGlobalSearchResult[]): CommandItem[] {
  return results.map((result) => ({
    label: result.title,
    subtitle: result.subtitle,
    relatedTo: result.relatedTo,
    category: ENTITY_CATEGORY_LABELS[result.type],
    route: buildEntityResultRoute(result),
  }));
}

@Component({
  selector: 'ic-command-palette',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    LucideUser,
    LucideCar,
    LucideShield,
    LucideCreditCard,
    LucideFileCheck,
    LucideUsers,
    LucideArrowRight,
    LucideBuilding,
    LucideHandshake,
  ],
  template: `
    @if (paletteService.isOpen()) {
      <div class="fixed inset-0 z-[1050] bg-black/40 flex justify-center items-start pt-[15vh]" (click)="close()">
        <div class="w-[560px] max-w-[90vw] bg-card rounded-xl shadow-[0_16px_70px_rgba(0,0,0,0.2)] overflow-hidden [font-family:var(--font-sans)]" (click)="$event.stopPropagation()">
          <div class="flex items-center gap-2 px-4 py-3.5">
            <svg class="text-gray-400 shrink-0" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
            <input
              #searchInput
              type="text"
              class="flex-1 border-none outline-none text-[15px] bg-transparent text-on-surface [font-family:inherit] placeholder:text-gray-400"
              placeholder="Type a command or search clients, policies, vehicles..."
              [ngModel]="searchQuery()"
              (ngModelChange)="onSearchChange($event)"
              (keydown)="onKeydown($event)"
            />
            <kbd class="inline-flex items-center justify-center px-1.5 py-0.5 text-[11px] [font-family:inherit] text-gray-400 bg-surface border border-divider rounded shrink-0">ESC</kbd>
          </div>
          <div class="h-px bg-divider"></div>
          <div class="max-h-[360px] overflow-y-auto p-2">
            @for (group of groupedItems(); track group.category) {
              <div class="mb-1">
                <div class="px-2 py-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{{ group.category }}</div>
                @for (item of group.items; track $index) {
                  <div
                    class="group flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer transition-colors duration-100 hover:bg-surface"
                    [class.bg-surface]="activeIndex() === getGlobalIndex(group.category, $index)"
                    (click)="navigate(item.route)"
                    (mouseenter)="activeIndex.set(getGlobalIndex(group.category, $index))"
                  >
                    @switch (item.category) {
                      @case ('Clients') {
                        <div class="flex items-center justify-center w-[30px] h-[30px] rounded-lg shrink-0 bg-[#eef1f5] text-[#4a5a72]">
                          <svg lucideUser class="w-4 h-4"></svg>
                        </div>
                      }
                      @case ('Vehicles') {
                        <div class="flex items-center justify-center w-[30px] h-[30px] rounded-lg shrink-0 bg-[#f6f1e7] text-[#8a6b2e]">
                          <svg lucideCar class="w-4 h-4"></svg>
                        </div>
                      }
                      @case ('Policies') {
                        <div class="flex items-center justify-center w-[30px] h-[30px] rounded-lg shrink-0 bg-[#f0eef5] text-[#5d5480]">
                          <svg lucideShield class="w-4 h-4"></svg>
                        </div>
                      }
                      @case ('Transactions') {
                        <div class="flex items-center justify-center w-[30px] h-[30px] rounded-lg shrink-0 bg-[#eef3ef] text-[#4a7360]">
                          <svg lucideCreditCard class="w-4 h-4"></svg>
                        </div>
                      }
                      @case ('Claims') {
                        <div class="flex items-center justify-center w-[30px] h-[30px] rounded-lg shrink-0 bg-[#f5eeef] text-[#8a4a52]">
                          <svg lucideFileCheck class="w-4 h-4"></svg>
                        </div>
                      }
                      @case ('Intermediaries') {
                        <div class="flex items-center justify-center w-[30px] h-[30px] rounded-lg shrink-0 bg-[#eef3f4] text-[#3f7480]">
                          <svg lucideUsers class="w-4 h-4"></svg>
                        </div>
                      }
                      @case ('Insurers') {
                        <div class="flex items-center justify-center w-[30px] h-[30px] rounded-lg shrink-0 bg-[#f2f0ec] text-[#6b6355]">
                          <svg lucideBuilding class="w-4 h-4"></svg>
                        </div>
                      }
                      @case ('Partners') {
                        <div class="flex items-center justify-center w-[30px] h-[30px] rounded-lg shrink-0 bg-[#f5efec] text-[#8a5a42]">
                          <svg lucideHandshake class="w-4 h-4"></svg>
                        </div>
                      }
                      @default {
                        <div class="flex items-center justify-center w-[30px] h-[30px] rounded-lg shrink-0 bg-surface text-gray-400">
                          <svg lucideArrowRight class="w-4 h-4"></svg>
                        </div>
                      }
                    }
                    <div class="flex-1 flex flex-col min-w-0">
                      <span class="text-sm text-on-surface">{{ item.label }}</span>
                      @if (item.subtitle) {
                        <span class="text-xs text-gray-400">{{ item.subtitle }}</span>
                      }
                      @if (item.relatedTo) {
                        <span class="text-[11px] text-indigo-500">{{ item.relatedTo }}</span>
                      }
                    </div>
                    <kbd
                      class="inline-flex items-center transition-opacity duration-100 text-gray-400 group-hover:opacity-100"
                      [class.opacity-100]="activeIndex() === getGlobalIndex(group.category, $index)"
                      [class.opacity-0]="activeIndex() !== getGlobalIndex(group.category, $index)"
                    >
                      <svg width="12" height="12" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 1 1 0 10h-2"/><line x1="8" x2="16" y1="12" y2="12"/></svg>
                    </kbd>
                  </div>
                }
              </div>
            }
            @if (searchLoading()) {
              <div class="flex items-center justify-center py-8 px-4 text-gray-400 text-sm">Searching...</div>
            }
            @if (searchError()) {
              <div class="flex items-center justify-center py-8 px-4 text-red-600 text-sm">
                Search failed. Try again.
              </div>
            }
            @if (!searchLoading() && !searchError() && groupedItems().length === 0) {
              <div class="flex items-center justify-center py-8 px-4 text-gray-400 text-sm">No results found</div>
            }
          </div>
        </div>
      </div>
    }
  `,
  styles: [],
})
export class IcCommandPaletteComponent implements AfterViewInit, OnDestroy {
  @ViewChildren('searchInput') searchInputRef!: QueryList<ElementRef<HTMLInputElement>>;

  items = input.required<CommandItem[]>();

  protected paletteService = inject(CommandPaletteService);
  private router = inject(Router);
  private searchService = inject(GlobalSearchService);

  searchQuery = signal('');
  activeIndex = signal(0);
  entityResults = signal<CommandItem[]>([]);
  searchLoading = signal(false);
  searchError = signal(false);

  private searchTerm$ = new Subject<string>();
  private searchSubscription: Subscription;

  groupedItems = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const allItems = this.items();
    const staticFiltered = query
      ? allItems.filter(
          (item) =>
            item.label.toLowerCase().includes(query) ||
            item.category.toLowerCase().includes(query) ||
            item.keywords?.some((k) => k.toLowerCase().includes(query)),
        )
      : allItems;

    const combined = query.length >= 2 ? [...staticFiltered, ...this.entityResults()] : staticFiltered;

    const grouped: { category: string; items: CommandItem[] }[] = [];
    for (const item of combined) {
      const existing = grouped.find((g) => g.category === item.category);
      if (existing) {
        existing.items.push(item);
      } else {
        grouped.push({ category: item.category, items: [item] });
      }
    }
    return grouped;
  });

  private categoryIndexMap = new Map<string, number>();

  constructor() {
    effect(() => {
      this.groupedItems();
      this.buildCategoryIndexMap();
    });

    // Reset on every open, regardless of what triggered it (Cmd+K or the header
    // search button) — both go through paletteService.open().
    effect(() => {
      if (this.paletteService.isOpen()) {
        this.searchQuery.set('');
        this.activeIndex.set(0);
      }
    });

    this.searchSubscription = this.searchTerm$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term.trim().length < 2) {
            this.searchLoading.set(false);
            this.searchError.set(false);
            return of<IGlobalSearchResult[]>([]);
          }
          this.searchLoading.set(true);
          this.searchError.set(false);
          return this.searchService.search(term).pipe(
            catchError(() => {
              this.searchError.set(true);
              return of<IGlobalSearchResult[]>([]);
            }),
          );
        }),
      )
      .subscribe((results) => {
        this.searchLoading.set(false);
        this.entityResults.set(resultsToCommandItems(results));
      });
  }

  ngAfterViewInit(): void {
    // The input only exists in the DOM while the palette is open (@if-gated), so the
    // native `autofocus` attribute doesn't fire reliably. QueryList.changes fires the
    // instant the input is inserted/removed, which is the precise moment to focus it.
    // @ViewChildren queries aren't populated until this hook, so it can't be wired
    // from the constructor.
    this.searchInputRef.changes.subscribe((list: QueryList<ElementRef<HTMLInputElement>>) => {
      list.first?.nativeElement.focus();
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  @HostListener('window:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.paletteService.toggle();
    }
  }

  close(): void {
    this.paletteService.close();
  }

  onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.activeIndex.set(0);
    this.searchTerm$.next(value);
  }

  onKeydown(event: KeyboardEvent): void {
    const totalItems = this.groupedItems().reduce((sum, g) => sum + g.items.length, 0);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex.update((i) => (i + 1) % totalItems);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex.update((i) => (i - 1 + totalItems) % totalItems);
        break;
      case 'Enter':
        event.preventDefault();
        this.navigateToActiveItem();
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
    }
  }

  navigate(route: string): void {
    this.close();
    this.router.navigateByUrl(route);
  }

  private buildCategoryIndexMap(): void {
    this.categoryIndexMap.clear();
    let index = 0;
    for (const group of this.groupedItems()) {
      this.categoryIndexMap.set(group.category, index);
      index += group.items.length;
    }
  }

  getGlobalIndex(category: string, localIndex: number): number {
    return (this.categoryIndexMap.get(category) ?? 0) + localIndex;
  }

  private navigateToActiveItem(): void {
    const allFiltered = this.groupedItems().flatMap((g) => g.items);
    const active = allFiltered[this.activeIndex()];
    if (active) {
      this.navigate(active.route);
    }
  }
}
